import React, { useState } from 'react';
import { useSelector } from "react-redux";
import { Search, Plus, RotateCcw, Edit, X, Trash2, User, Settings, Share2, Send, ChevronDown } from 'lucide-react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useGetPatientsQuery, useUpdatePatientMutation, useCreatePatientMutation, useDeletePatientMutation, useGetProfessionalsQuery } from '../../services/api/kinesioApi.js';
import { toast } from '../ui/use-toast';
import SharePatientModal from './SharePatientModal.jsx';
import DatePicker, { registerLocale } from 'react-datepicker';
import "react-datepicker/dist/react-datepicker.css";
import es from 'date-fns/locale/es';

registerLocale('es', es);

const parseDateString = (dateString) => {
    if (!dateString) return null;
    const [year, month, day] = dateString.split('-');
    return new Date(year, month - 1, day);
};

const formatDateString = (date) => {
    if (!date) return '';
    const year = date.getFullYear();
    const month = String(date.getMonth() + 1).padStart(2, '0');
    const day = String(date.getDate()).padStart(2, '0');
    return `${year}-${month}-${day}`;
};

export const isPausesPatient = (p) => Boolean(
  p?.is_pauses === true || 
  p?.is_pauses === 'true' || 
  p?.is_pauses === 1 || 
  p?.admissionData?.is_pauses === true || 
  p?.admissionData?.is_pauses === 'true' || 
  p?.admissionData?.is_pauses === 1
);

const PatientList = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const user = useSelector(state => state.authSlice.userInfo);
  const [mainTab, setMainTab] = useState('pauses'); // 'pauses' | 'particulares' | 'todos'
  const [activeFilter, setActiveFilter] = useState('Todos');
  const [searchQuery, setSearchQuery] = useState('');

  React.useEffect(() => {
    if (location.state?.openNewModal && user?.role === 'ADMIN') {
      const prefill = location.state.prefill || {};
      setEditingPatient({
        nombre: prefill.nombre || '',
        dni: prefill.dni || '',
        fecha_nacimiento: '',
        gender: '',
        blood_type: '',
        status: 'activo',
        is_pauses: true,
        email: prefill.datos_contacto?.email || '',
        phone: prefill.datos_contacto?.telefono || prefill.datos_contacto?.phone || '',
        professionalIds: user?.id ? [user.id] : []
      });
      setIsModalOpen(true);
      window.history.replaceState({}, document.title);
    }
  }, [location.state, user?.role, user?.id]);

  const { data: patients = [], isLoading } = useGetPatientsQuery();
  const [updatePatient, { isLoading: isUpdating }] = useUpdatePatientMutation();
  const [createPatient, { isLoading: isCreating }] = useCreatePatientMutation();
  const [deletePatient] = useDeletePatientMutation();
  const { data: professionalsResponse } = useGetProfessionalsQuery();
  const professionals = (professionalsResponse?.data || []).filter(p => 
    Boolean(p.is_active) && 
    p.is_active !== 'false' && 
    p.is_active !== 0 &&
    (p.role === 'ADMIN' || p.role === 'EMPLOYEE') &&
    p.role !== 'USER' &&
    p.role !== 'PATIENT'
  );

  const visiblePatients = patients.filter(p => {
    if (isPausesPatient(p)) return true;
    if (user?.role === 'ADMIN') return true;
    if (!user?.id) return false;
    const assignedIds = (p.professionals || []).map(prof => prof.id);
    return assignedIds.includes(user.id);
  });

  const pausesCount = visiblePatients.filter(isPausesPatient).length;
  const particularesCount = visiblePatients.filter(p => !isPausesPatient(p)).length;

  const [isModalOpen, setIsModalOpen] = useState(false);
  const [editingPatient, setEditingPatient] = useState(null);
  const [patientToShare, setPatientToShare] = useState(null);

  const filters = ['Todos', 'Visitas Recientes', 'Requiere Seguimiento', 'Nuevos esta Semana'];

  const filteredPatients = visiblePatients.filter(p => {
      if (!p || !p.nombre) return false;

      // 0. Primary Segmented Tab Filter (Pauses vs Particulares)
      if (mainTab === 'pauses' && !isPausesPatient(p)) return false;
      if (mainTab === 'particulares' && isPausesPatient(p)) return false;

      // 1. Text Search Filter
      const query = searchQuery.trim().toLowerCase();
      if (query) {
          const matchesName = p.nombre.toLowerCase().includes(query);
          const matchesId = p.id.toString().includes(query);
          const matchesDni = p.dni ? p.dni.includes(query) : false;
          const matchesEmail = p.datos_contacto?.email ? p.datos_contacto.email.toLowerCase().includes(query) : false;
          const matchesPhone = p.datos_contacto?.phone ? p.datos_contacto.phone.includes(query) : false;
          
          if (!matchesName && !matchesId && !matchesDni && !matchesEmail && !matchesPhone) {
              return false;
          }
      }

      // 2. Active Secondary Filter Chips
      if (activeFilter === 'Todos') {
          return true;
      }

      const now = new Date();
      const createdAt = p.createdAt ? new Date(p.createdAt) : p.created_at ? new Date(p.created_at) : null;
      const updatedAt = p.updatedAt ? new Date(p.updatedAt) : p.updated_at ? new Date(p.updated_at) : null;
      const statusLower = (p.status || '').toLowerCase();

      if (activeFilter === 'Visitas Recientes') {
          // Visita o actualización dentro de los últimos 30 días, o estado activo/reciente/tratamiento
          const thirtyDaysAgo = new Date(now.getTime() - 30 * 24 * 60 * 60 * 1000);
          const isRecentlyUpdated = updatedAt && updatedAt >= thirtyDaysAgo;
          const isRecentlyCreated = createdAt && createdAt >= thirtyDaysAgo;
          const isActiveStatus = statusLower.includes('activo') || statusLower.includes('tratamiento') || statusLower.includes('visita');
          
          return isRecentlyUpdated || isRecentlyCreated || isActiveStatus;
      }

      if (activeFilter === 'Requiere Seguimiento') {
          // Si el estado indica seguimiento o pendiente, o si han pasado más de 14 días desde la última actualización sin estar de alta/inactivo
          const isSeguimientoStatus = statusLower.includes('seguimiento') || 
                                      statusLower.includes('pendiente') || 
                                      statusLower.includes('evalua') || 
                                      statusLower.includes('revision');
          
          const fourteenDaysAgo = new Date(now.getTime() - 14 * 24 * 60 * 60 * 1000);
          const isStale = (updatedAt && updatedAt <= fourteenDaysAgo) || (!updatedAt && createdAt && createdAt <= fourteenDaysAgo);
          const isNotInactive = !statusLower.includes('alta') && !statusLower.includes('inactivo') && !statusLower.includes('completado');

          return isSeguimientoStatus || (isStale && isNotInactive);
      }

      if (activeFilter === 'Nuevos esta Semana') {
          // Creado en los últimos 7 días
          const sevenDaysAgo = new Date(now.getTime() - 7 * 24 * 60 * 60 * 1000);
          return createdAt && createdAt >= sevenDaysAgo;
      }

      return true;
  }).sort((a, b) => a.nombre.localeCompare(b.nombre));

  const calculateAge = (dob) => {
      if (!dob) return 'N/A';
      const diff = Date.now() - new Date(dob).getTime();
      return Math.abs(new Date(diff).getUTCFullYear() - 1970);
  };

  const getInitials = (name) => {
      if (!name) return '??';
      return name.split(' ').map(n => n[0]).join('').substring(0, 2).toUpperCase();
  };

  const handleOpenModal = (patient = null) => {
      if (patient) {
          setEditingPatient({
              id: patient.id,
              nombre: patient.nombre || '',
              dni: patient.dni || '',
              fecha_nacimiento: patient.fecha_nacimiento ? patient.fecha_nacimiento.split('T')[0] : '',
              gender: patient.gender || '',
              blood_type: patient.blood_type || '',
              status: patient.status || '',
              is_pauses: isPausesPatient(patient),
              admissionData: patient.admissionData || {},
              email: patient.datos_contacto?.email || '',
              phone: patient.datos_contacto?.phone || '',
              professionalIds: patient.professionals ? patient.professionals.map(p => p.id) : []
          });
      } else {
          setEditingPatient({
              nombre: '',
              dni: '',
              fecha_nacimiento: '',
              gender: '',
              blood_type: '',
              status: '',
              is_pauses: mainTab !== 'particulares',
              email: '',
              phone: '',
              professionalIds: user?.id ? [user.id] : []
          });
      }
      setIsModalOpen(true);
  };

  const handleSave = async (e) => {
      e.preventDefault();
      try {
          const isPausesVal = Boolean(editingPatient.is_pauses);
          const payload = {
              nombre: editingPatient.nombre,
              dni: editingPatient.dni || null,
              fecha_nacimiento: editingPatient.fecha_nacimiento || null,
              gender: editingPatient.gender,
              blood_type: editingPatient.blood_type,
              status: editingPatient.status,
              is_pauses: isPausesVal,
              admissionData: {
                  ...(editingPatient.admissionData || {}),
                  is_pauses: isPausesVal
              },
              datos_contacto: {
                  email: editingPatient.email,
                  phone: editingPatient.phone
              },
              professionalIds: editingPatient.professionalIds
          };

          if (editingPatient.id) {
              await updatePatient({ id: editingPatient.id, ...payload }).unwrap();
          } else {
              await createPatient(payload).unwrap();
          }
          setIsModalOpen(false);
      } catch (error) {
          console.error("Error saving patient", error);
          toast({ title: 'Error', description: 'Error al guardar paciente', variant: 'destructive' });
      }
  };

  const [patientToDelete, setPatientToDelete] = useState(null);
  const [deleteError, setDeleteError] = useState(null);

  const confirmDelete = async () => {
    if (!patientToDelete) return;
    try {
      setDeleteError(null);
      await deletePatient(patientToDelete.id).unwrap();
      setPatientToDelete(null);
    } catch (error) {
      console.error('Failed to delete patient:', error);
      setDeleteError('No se pudo eliminar el paciente. Verifica que no tenga registros bloqueantes.');
    }
  };

  const handleDeletePatient = (patient) => {
    setPatientToDelete(patient);
  };

  return (
    <div className="w-full flex flex-col min-h-full bg-white p-4 md:p-8 relative">
      {/* Header */}
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h1 className="text-3xl font-bold tracking-tight text-[#111827]">Pacientes</h1>
          <p className="text-gray-500 mt-1">Gestiona el historial y datos de tus pacientes.</p>
        </div>
        <div className="flex items-center gap-3 w-full md:w-auto flex-wrap md:flex-nowrap">
          <div className="relative flex-1 md:w-64">
            <Search className="absolute left-3 top-1/2 -translate-y-1/2 text-gray-400" size={18} />
            <input 
              type="text" 
              placeholder="Buscar por nombre o ID..." 
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="w-full pl-10 pr-4 py-2.5 bg-gray-50 border-none rounded-lg text-sm focus:outline-none focus:ring-2 focus:ring-[#0A58CA] transition-shadow"
            />
          </div>
          <button 
            onClick={() => navigate('/historial?mode=template')}
            className="bg-purple-50 hover:bg-purple-100 text-purple-700 border border-purple-200 px-4 py-2.5 rounded-lg font-semibold shadow-xs transition-all flex items-center gap-2 text-sm whitespace-nowrap"
            title="Editar estructura del historial médico"
          >
            <Settings size={18} className="text-purple-600" /> Editar Estructura Historial
          </button>
          {user?.role === 'ADMIN' && (
            <button 
              onClick={() => handleOpenModal()}
              className="bg-[#0A58CA] hover:bg-blue-700 text-white px-5 py-2.5 rounded-lg font-semibold shadow-sm transition-all flex items-center gap-2 text-sm whitespace-nowrap"
            >
              <Plus size={18} /> Nuevo Paciente
            </button>
          )}
        </div>
      </div>

      {/* Primary Segmented Tabs: Pacientes Pauses vs Pacientes Particulares */}
      <div className="flex border-b border-gray-200 mb-6 gap-2 sm:gap-6 overflow-x-auto">
        <button
          onClick={() => { setMainTab('pauses'); setActiveFilter('Todos'); }}
          className={`pb-3 font-bold text-sm sm:text-base flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
            mainTab === 'pauses'
              ? 'border-emerald-600 text-emerald-800'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          <img src="/images/pausesLogo.png" alt="Pauses" className="h-4 w-auto object-contain" />
          <span>Pacientes Pauses</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-black ${
            mainTab === 'pauses' ? 'bg-emerald-100 text-emerald-800' : 'bg-gray-100 text-gray-600'
          }`}>
            {pausesCount}
          </span>
        </button>

        <button
          onClick={() => { setMainTab('particulares'); setActiveFilter('Todos'); }}
          className={`pb-3 font-bold text-sm sm:text-base flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
            mainTab === 'particulares'
              ? 'border-blue-600 text-blue-700'
              : 'border-transparent text-gray-500 hover:text-gray-800'
          }`}
        >
          <User size={16} />
          <span>Pacientes Particulares (Privados)</span>
          <span className={`px-2 py-0.5 rounded-full text-xs font-black ${
            mainTab === 'particulares' ? 'bg-blue-100 text-blue-700' : 'bg-gray-100 text-gray-600'
          }`}>
            {particularesCount}
          </span>
        </button>

        <button
          onClick={() => { setMainTab('todos'); setActiveFilter('Todos'); }}
          className={`pb-3 font-semibold text-sm sm:text-base flex items-center gap-2 border-b-2 transition-all whitespace-nowrap ${
            mainTab === 'todos'
              ? 'border-gray-800 text-gray-900'
              : 'border-transparent text-gray-400 hover:text-gray-700'
          }`}
        >
          <span>Todos</span>
          <span className="px-2 py-0.5 rounded-full text-xs font-semibold bg-gray-100 text-gray-600">
            {patients.length}
          </span>
        </button>
      </div>

      {/* Filter Chips */}
      <div className="flex flex-wrap items-center gap-3 mb-8">
        {filters.map(filter => (
          <button
            key={filter}
            onClick={() => setActiveFilter(filter)}
            className={`px-4 py-1.5 rounded-full text-sm font-semibold transition-colors border ${
              activeFilter === filter 
                ? 'bg-[#F3E8FF] text-[#6D28D9] border-transparent' 
                : 'bg-white text-gray-600 border-gray-200 hover:bg-gray-50'
            }`}
          >
            {filter}
          </button>
        ))}
      </div>

      {isLoading ? (
          <div className="text-center py-10 text-gray-500">Cargando pacientes...</div>
      ) : filteredPatients.length === 0 ? (
          <div className="bg-white border border-gray-100 rounded-2xl p-12 text-center text-gray-500 shadow-xs">
              <User className="w-12 h-12 text-purple-300 mx-auto mb-3" />
              <p className="font-semibold text-gray-800 text-base">No se encontraron pacientes</p>
              <p className="text-xs text-gray-400 mt-1">No hay pacientes que coincidan con el filtro "{activeFilter}"{searchQuery ? ` y la búsqueda "${searchQuery}"` : ''}.</p>
              <button 
                  onClick={() => { setMainTab('todos'); setActiveFilter('Todos'); setSearchQuery(''); }}
                  className="mt-4 px-4 py-2 bg-purple-50 text-[#6D28D9] rounded-lg text-xs font-bold hover:bg-purple-100 transition-colors"
              >
                  Ver todos los pacientes
              </button>
          </div>
      ) : (
          <div className="bg-white border border-gray-100 rounded-2xl overflow-hidden shadow-[0_4px_20px_rgb(0,0,0,0.03)]">
            <div className="overflow-x-auto">
              <table className="w-full text-left border-collapse">
                <thead>
                  <tr className="bg-gray-50 border-b border-gray-100 text-gray-500 text-xs uppercase tracking-wider font-semibold">
                    <th className="p-4 pl-6">Paciente</th>
                    <th className="p-4">Contacto</th>
                    <th className="p-4">Detalles</th>
                    <th className="p-4">Estado</th>
                    <th className="p-4 pr-6 text-right">Acciones</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-gray-100">
                  {filteredPatients.map((patient) => {
                    const isPauses = isPausesPatient(patient);
                    return (
                    <tr 
                      key={patient.id} 
                      onClick={() => navigate(`/pacientes/${patient.id}`)}
                      className="hover:bg-gray-50/50 transition-colors cursor-pointer"
                    >
                      <td className="p-4 pl-6">
                        <div className="flex items-center gap-3">
                          {/* Avatar del usuario con foto del logo de pauses al lado */}
                          <div className="flex items-center gap-2 shrink-0">
                            <div className="w-10 h-10 rounded-full flex items-center justify-center font-bold text-sm bg-[#EDE9FE] text-[#6D28D9] shadow-2xs">
                              {getInitials(patient.nombre)}
                            </div>

                            {/* Al lado del logo del usuario: Foto del logo de Pauses o indicador Particular */}
                            {isPauses ? (
                              <div 
                                className="w-8 h-8 rounded-full bg-white border-2 border-emerald-400 p-0.5 shadow-xs flex items-center justify-center shrink-0 hover:scale-105 transition-transform" 
                                title="Paciente de Pauses"
                              >
                                <img 
                                  src="/images/pausesLogo.png" 
                                  alt="Pauses" 
                                  className="w-full h-full object-contain rounded-full" 
                                />
                              </div>
                            ) : (
                              <div 
                                className="w-8 h-8 rounded-full bg-slate-100 border border-slate-200 shadow-2xs flex items-center justify-center text-slate-400 shrink-0" 
                                title="Paciente de Profesional Particular"
                              >
                                <User size={15} className="text-slate-500" />
                              </div>
                            )}
                          </div>

                          <div>
                            <div className="flex items-center gap-2 flex-wrap">
                              <span className="font-bold text-gray-900">{patient.nombre}</span>
                              {isPauses ? (
                                <span 
                                  className="inline-flex items-center gap-1.5 px-2 py-0.5 rounded-md text-[11px] font-semibold bg-[#ECFDF5] text-[#065F46] border border-[#A7F3D0] shadow-2xs"
                                  title="Paciente de Pauses"
                                >
                                  <img 
                                    src="/images/pausesLogo.png" 
                                    alt="Pauses" 
                                    className="h-3.5 w-auto object-contain" 
                                  />
                                  <span>Pauses</span>
                                </span>
                              ) : (
                                <span 
                                  className="inline-flex items-center px-2 py-0.5 rounded-md text-[11px] font-medium bg-slate-100 text-slate-700 border border-slate-200"
                                  title="Paciente de Profesional Particular"
                                >
                                  <span>Particular</span>
                                </span>
                              )}
                            </div>
                            <div className="text-xs text-gray-500">ID: {patient.id}</div>
                          </div>
                        </div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm font-medium text-gray-900">{patient.datos_contacto?.phone || '-'}</div>
                        <div className="text-xs text-gray-500">{patient.datos_contacto?.email || '-'}</div>
                      </td>
                      <td className="p-4">
                        <div className="text-sm text-gray-900">{calculateAge(patient.fecha_nacimiento)} años</div>
                        <div className="text-xs text-gray-500">{patient.gender || '-'} • {patient.blood_type || '-'}</div>
                      </td>
                      <td className="p-4" onClick={(e) => e.stopPropagation()}>
                        <div className="relative inline-flex items-center">
                          <select 
                            value={patient.status || ''} 
                            onChange={async (e) => {
                              const newStatus = e.target.value;
                              try {
                                await updatePatient({ id: patient.id, status: newStatus }).unwrap();
                                toast({ title: 'Estado Actualizado', description: `El estado de ${patient.nombre} se actualizó a "${newStatus || 'Sin Estado'}".`, variant: 'success' });
                              } catch (err) {
                                toast({ title: 'Error', description: 'Error al actualizar el estado.', variant: 'destructive' });
                              }
                            }}
                            className={`appearance-none cursor-pointer text-xs font-semibold px-3 py-1 pr-6 rounded-full border transition-all outline-none ${
                              !patient.status 
                                ? 'bg-gray-100 text-gray-500 border-gray-200 hover:bg-gray-200' 
                                : patient.status.toLowerCase().includes('activo') 
                                ? 'bg-blue-100 text-blue-700 border-blue-200 hover:bg-blue-200'
                                : patient.status.toLowerCase().includes('tratamiento')
                                ? 'bg-emerald-100 text-emerald-700 border-emerald-200 hover:bg-emerald-200'
                                : patient.status.toLowerCase().includes('seguimiento') || patient.status.toLowerCase().includes('pendiente')
                                ? 'bg-purple-100 text-purple-700 border-purple-200 hover:bg-purple-200'
                                : patient.status.toLowerCase().includes('alta')
                                ? 'bg-green-100 text-green-700 border-green-200 hover:bg-green-200'
                                : 'bg-blue-50 text-blue-600 border-blue-100 hover:bg-blue-100'
                            }`}
                          >
                            <option value="">- Sin Estado -</option>
                            <option value="Activo">Activo</option>
                            <option value="En tratamiento">En tratamiento</option>
                            <option value="Requiere Seguimiento">Requiere Seguimiento</option>
                            <option value="Alta">Alta</option>
                            <option value="Inactivo">Inactivo</option>
                          </select>
                          <ChevronDown size={12} className="pointer-events-none absolute right-2 text-current opacity-70" />
                        </div>
                      </td>
                      <td className="p-4 pr-6 text-right">
                        <div className="flex justify-end gap-2">
                          <button 
                            onClick={(e) => { e.stopPropagation(); navigate(`/pacientes/${patient.id}`); }}
                            className="p-2 rounded-lg text-[#0A58CA] hover:bg-blue-50 transition-colors border border-transparent hover:border-blue-100"
                            title="Ver Perfil / Acceder a la info del Paciente"
                          >
                            <User size={18} />
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); setPatientToShare(patient); }}
                            className="p-2 rounded-lg text-indigo-600 hover:bg-indigo-50 transition-colors border border-transparent hover:border-indigo-100"
                            title="Enviar Info / Compartir Historial Médico a otro profesional"
                          >
                            <Send size={18} />
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleOpenModal(patient); }}
                            className="p-2 rounded-lg text-gray-600 hover:bg-gray-100 transition-colors border border-transparent hover:border-gray-200"
                            title="Editar Paciente"
                          >
                            <Edit size={18} />
                          </button>
                          <button 
                            onClick={(e) => { e.stopPropagation(); handleDeletePatient(patient); }}
                            className="p-2 rounded-lg text-red-500 hover:bg-red-50 transition-colors border border-transparent hover:border-red-100"
                            title="Eliminar Paciente"
                          >
                            <Trash2 size={18} />
                          </button>
                        </div>
                      </td>
                    </tr>
                    );
                  })}
                </tbody>
              </table>
              {filteredPatients.length === 0 && (
                <div className="p-8 text-center text-gray-500">No se encontraron pacientes.</div>
              )}
            </div>
          </div>
      )}

      {/* Edit/Create Modal */}
      {isModalOpen && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm flex items-center justify-center z-50 p-4">
              <div className="bg-white rounded-2xl shadow-xl w-full max-w-lg flex flex-col max-h-[90vh] overflow-hidden animate-in fade-in zoom-in-95 duration-200">
                  <div className="flex justify-between items-center p-5 border-b border-gray-100 shrink-0">
                      <h3 className="font-bold text-lg text-gray-900">
                          {editingPatient?.id ? 'Editar Paciente' : 'Nuevo Paciente'}
                      </h3>
                      <button type="button" onClick={() => setIsModalOpen(false)} className="text-gray-400 hover:text-gray-600"><X size={20}/></button>
                  </div>
                  <form onSubmit={handleSave} className="p-5 flex flex-col gap-4 overflow-y-auto">
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Nombre Completo *</label>
                              <input 
                                  type="text" 
                                  required
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500"
                                  value={editingPatient?.nombre || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, nombre: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">DNI</label>
                              <input 
                                  type="text" 
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500"
                                  value={editingPatient?.dni || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, dni: e.target.value})}
                              />
                          </div>
                      </div>
                      
                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Email</label>
                              <input 
                                  type="email" 
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500"
                                  value={editingPatient?.email || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, email: e.target.value})}
                              />
                          </div>
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Teléfono</label>
                              <input 
                                  type="text" 
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500"
                                  value={editingPatient?.phone || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, phone: e.target.value})}
                              />
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Fecha de Nacimiento</label>
                              <DatePicker
                                  selected={parseDateString(editingPatient?.fecha_nacimiento)}
                                  onChange={(date) => setEditingPatient({...editingPatient, fecha_nacimiento: formatDateString(date)})}
                                  dateFormat="dd/MM/yyyy"
                                  locale="es"
                                  showMonthDropdown
                                  showYearDropdown
                                  dropdownMode="select"
                                  placeholderText="Seleccione fecha"
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500"
                                  wrapperClassName="w-full"
                              />
                          </div>
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Género</label>
                              <select 
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                                  value={editingPatient?.gender || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, gender: e.target.value})}
                              >
                                  <option value="">Seleccione</option>
                                  <option value="M">Masculino</option>
                                  <option value="F">Femenino</option>
                                  <option value="Otro">Otro</option>
                              </select>
                          </div>
                      </div>

                      <div className="grid grid-cols-2 gap-4">
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Sangre</label>
                              <select 
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                                  value={editingPatient?.blood_type || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, blood_type: e.target.value})}
                              >
                                  <option value="">Seleccione</option>
                                  <option value="A+">A+</option>
                                  <option value="A-">A-</option>
                                  <option value="B+">B+</option>
                                  <option value="B-">B-</option>
                                  <option value="AB+">AB+</option>
                                  <option value="AB-">AB-</option>
                                  <option value="O+">O+</option>
                                  <option value="O-">O-</option>
                              </select>
                          </div>
                          <div>
                              <label className="block text-sm font-semibold text-gray-700 mb-1">Estado</label>
                              <select 
                                  className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                                  value={editingPatient?.status || ''}
                                  onChange={(e) => setEditingPatient({...editingPatient, status: e.target.value})}
                              >
                                  <option value="">- Sin Estado -</option>
                                  <option value="Activo">Activo</option>
                                  <option value="En tratamiento">En tratamiento</option>
                                  <option value="Requiere Seguimiento">Requiere Seguimiento</option>
                                  <option value="Alta">Alta</option>
                                  <option value="Inactivo">Inactivo</option>
                              </select>
                          </div>
                      </div>

                      <div>
                          <label className="block text-sm font-semibold text-gray-700 mb-1">Tipo de Paciente</label>
                          <select 
                              className="w-full border border-gray-200 rounded-lg p-2.5 text-sm focus:outline-none focus:border-blue-500 bg-white"
                              value={editingPatient?.is_pauses ? 'pauses' : 'individual'}
                              onChange={(e) => setEditingPatient({...editingPatient, is_pauses: e.target.value === 'pauses'})}
                          >
                              <option value="pauses">Paciente de Pauses (Público para Administradores)</option>
                              <option value="individual">Paciente Particular (Privado del Profesional)</option>
                          </select>
                      </div>

                      {['ADMIN', 'EMPLOYEE'].includes(user?.role) && (
                          <div className="grid grid-cols-1 gap-4">
                              <div>
                                  <label className="block text-sm font-semibold text-gray-700 mb-2">Profesionales Asignados</label>
                                  <div className="flex flex-col gap-2 max-h-40 overflow-y-auto border border-gray-200 rounded-lg p-3 bg-gray-50">
                                      {professionals.map(prof => (
                                          <label key={prof.id} className="flex items-center gap-2 text-sm text-gray-700 cursor-pointer">
                                              <input 
                                                  type="checkbox" 
                                                  className="rounded text-[#0A58CA] focus:ring-[#0A58CA]"
                                                  checked={editingPatient?.professionalIds?.includes(prof.id) || false}
                                                  onChange={(e) => {
                                                      const newIds = e.target.checked 
                                                          ? [...(editingPatient.professionalIds || []), prof.id]
                                                          : (editingPatient.professionalIds || []).filter(id => id !== prof.id);
                                                      setEditingPatient({...editingPatient, professionalIds: newIds});
                                                  }}
                                              />
                                              {prof.name}
                                          </label>
                                      ))}
                                      {professionals.length === 0 && (
                                          <span className="text-gray-400 text-sm">No hay profesionales disponibles.</span>
                                      )}
                                  </div>
                              </div>
                          </div>
                      )}

                      <div className="mt-4 flex justify-end gap-2">
                          <button 
                              type="button" 
                              onClick={() => setIsModalOpen(false)}
                              className="px-4 py-2.5 text-gray-600 font-semibold hover:bg-gray-100 rounded-lg"
                          >
                              Cancelar
                          </button>
                          <button 
                              type="submit" 
                              disabled={isCreating || isUpdating}
                              className="px-5 py-2.5 bg-blue-600 hover:bg-blue-700 text-white font-semibold rounded-lg disabled:opacity-50 transition-colors"
                          >
                              {isCreating || isUpdating ? 'Guardando...' : 'Guardar Paciente'}
                          </button>
                      </div>
                  </form>
              </div>
          </div>
      )}

      {/* Delete Confirmation Modal */}
      {patientToDelete && (
          <div className="fixed inset-0 bg-black/40 backdrop-blur-sm z-50 flex items-center justify-center p-4">
              <div className="bg-white rounded-2xl w-full max-w-md p-6 shadow-xl animate-in fade-in zoom-in duration-200">
                  <div className="flex items-center justify-center w-12 h-12 rounded-full bg-red-100 mb-4 mx-auto">
                      <Trash2 className="text-red-600" size={24} />
                  </div>
                  <h2 className="text-xl font-bold text-center text-gray-900 mb-2">Eliminar Paciente</h2>
                  <p className="text-center text-gray-500 mb-6">
                      ¿Estás seguro de que quieres eliminar a <span className="font-bold text-gray-700">{patientToDelete.nombre}</span>? 
                      Se eliminarán también sus turnos y su historial clínico. Esta acción no se puede deshacer.
                  </p>
                  
                  {deleteError && (
                      <div className="bg-red-50 text-red-600 text-sm font-medium p-3 rounded-lg mb-4 text-center">
                          {deleteError}
                      </div>
                  )}

                  <div className="flex justify-center gap-3">
                      <button 
                          onClick={() => {
                              setPatientToDelete(null);
                              setDeleteError(null);
                          }}
                          className="flex-1 px-5 py-2.5 text-gray-700 font-semibold bg-gray-100 hover:bg-gray-200 rounded-xl transition-colors"
                      >
                          Cancelar
                      </button>
                      <button 
                          onClick={confirmDelete}
                          className="flex-1 px-5 py-2.5 bg-red-600 hover:bg-red-700 text-white font-semibold rounded-xl transition-colors"
                      >
                          Sí, eliminar
                      </button>
                  </div>
              </div>
          </div>
      )}

      {/* Share Patient Modal */}
      <SharePatientModal 
        patient={patientToShare} 
        isOpen={!!patientToShare} 
        onClose={() => setPatientToShare(null)} 
      />

    </div>
  );
};

export default PatientList;
